/* eslint-disable no-restricted-globals */
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import moment from "moment";
import { ApiResponse, ApiResponseResult } from "middleware/api";
import { InvoiceFormType } from "pages/detailPreliquidation/invoice/types";

import {
  call,
  CallEffect,
  put,
  delay,
  PutEffect,
  takeLatest,
  all,
  AllEffect,
} from "redux-saga/effects";
import {
  BULK_FLOW_ID,
  DATE_FORMATS,
  FIRST_ANIMATION_TIME,
  PRELIQUIDATION_STATUS_OK,
} from "utils/constants";
import * as preliquidationsMiddleware from "../middleware/preliquidations";
import { actions as notificationActions } from "../reducers/notification";
import { actions as preliquidationActions } from "../reducers/preliquidation";
import {
  AdjustAmountMiddlewareType,
  AdjustmenResponseType,
  DetailPreliquidationBodyParamsType,
  DetailPreliquidationsApiResponseType,
  DetailPreliquidationsContentResponseType,
  DetailPreliquidationsInvoiceApiResponseType,
  DetailPreliquidationsInvoiceTypesApiResponseType,
  DetatilPreliquidationsApiResponse,
  PreliquidationCastParamsMiddlewareType,
  PreliquidationParamsMiddlewareType,
  PreliquidationsApiResponse,
  RejectInvoiceMiddlewareType,
  RejectInvoiceReasonType,
  sendAccountinMiddlewareType,
  UploadInvoiceFileMiddlewareType,
} from "./types/preliquidation";
import i18next from "config/i18next";
import { NotificationStateType } from "reducers/types/notification";
import { replace } from "connected-react-router";
import { LoadingButtonState } from "component/loadingButton/types";
import { store } from "store";
import { connectionErrorAlert } from "utils/alerts";
import { serverErrorAlert } from "utils/alerts";
import { CsvResponseType } from "./types/pickers";
import { createCSV } from "utils/files";
import { t } from "i18next";

const sagas = [
  takeLatest(
    preliquidationActions.getRejectedReasonRequest.type,
    getRejectedReason
  ),
  takeLatest(
    preliquidationActions.getPreliquidationsRequest.type,
    getPreliquidations
  ),
  takeLatest(
    preliquidationActions.getPreliquidationsExportRequest.type,
    getPreliquidationsExport
  ),
  takeLatest(
    preliquidationActions.getDetailPreliquidationsRequest.type,
    getDetailPreliquidations
  ),
  takeLatest(
    preliquidationActions.getMorePreliquidationsRequest.type,
    getMorePreliquidations
  ),
  takeLatest(
    preliquidationActions.getInvoiceDetailRequest.type,
    getInvoiceDetail
  ),
  takeLatest(
    preliquidationActions.getInvoiceDetailSaveRequest.type,
    putSaveDetailInvoice
  ),
  takeLatest(
    preliquidationActions.getInvoiceDetailApproveFetch.type,
    patchApproveDetailInvoice
  ),
  takeLatest(
    preliquidationActions.deleteInvoiceDetailRequest.type,
    putRejectDetailInvoice
  ),
  takeLatest(preliquidationActions.sendAccountingRequest.type, sendAccounting),
  takeLatest(
    preliquidationActions.replaceInvoiceFileFetch.type,
    replaceInvoiceFile
  ),
  takeLatest(
    preliquidationActions.replaceInvoiceFileTextFetch.type,
    replaceInvoiceFileText
  ),
  takeLatest(
    preliquidationActions.uploadInvoiceFileFetch.type,
    uploadInvoiceFile
  ),
  takeLatest(
    preliquidationActions.uploadInvoiceFileTextFetch.type,
    uploadInvoiceFileText
  ),
  takeLatest(
    preliquidationActions.deleteInvoiceFileRequest.type,
    deleteInvoiceFile
  ),
  takeLatest(
    preliquidationActions.getInvoiceDetailTypesRequest.type,
    getDetailInvoiceTypes
  ),
  takeLatest(preliquidationActions.adjustAmountFetch.type, adjustAmount),
  takeLatest(
    preliquidationActions.getPendingApprovalPresettlementsRequest,
    getPendingApprovalPresettlements
  ),
];

export default sagas;

const process = (body: DetailPreliquidationBodyParamsType) => {
  return {
    ...body,
    emisionDate: body?.emisionDate
      ? moment(body?.emisionDate, DATE_FORMATS.shortDate).format(
          DATE_FORMATS.shortISODate
        )
      : null,
  };
};

const processDatePicker = (
  payload: PreliquidationParamsMiddlewareType
): PreliquidationCastParamsMiddlewareType => {
  let payloadCast: PreliquidationCastParamsMiddlewareType =
    payload as PreliquidationCastParamsMiddlewareType;

  const { generatedAt } = payload;
  if (!generatedAt) delete payloadCast.generatedAt;
  else {
    payloadCast = {
      ...payload,
      generatedAt: moment(generatedAt, DATE_FORMATS.shortDate).format(
        DATE_FORMATS.shortISODate
      ),
    };
  }

  return payloadCast;
};

function* getPreliquidationsExport({
  payload,
}: PayloadAction<PreliquidationCastParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PreliquidationsApiResponse>>
  | PutEffect<{ type: string }>,
  void,
  CsvResponseType
> {
  let payloadCast: PreliquidationCastParamsMiddlewareType =
    processDatePicker(payload);
  const response = yield call(preliquidationsMiddleware.getPreliquidationsExport, payloadCast);

  if (response.status !== 200) {
    yield put(preliquidationActions.getPreliquidationsExportError());
  } else {
    createCSV(response.data, "presettlements");
    yield put(preliquidationActions.getPreliquidationsExportSuccess());
    yield put(
      notificationActions.showNotification({
        id: "notification-export-success",
        level: "success",
        title: i18next.t("alerts:exportTitle"),
        body: i18next.t("alerts:exportMessage"),
      })
    );
  }
}

function* getPreliquidations({
  payload,
}: PayloadAction<PreliquidationParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PreliquidationsApiResponse>>
  | PutEffect<{ type: string }>,
  void,
  PreliquidationsApiResponse
> {
  if (payload?.status === "") {
    delete payload["status"];
  }
  if (payload?.regions === "") {
    delete payload["regions"];
  }
  let payloadCast: PreliquidationCastParamsMiddlewareType =
    processDatePicker(payload);
  const response = yield call(
    preliquidationsMiddleware.getPreliquidations,
    payloadCast
  );
  if (response.status !== 200) {
    yield put(preliquidationActions.getPreliquidationsError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    yield put(preliquidationActions.getPreliquidationsSuccess(response.data));
    const isExportDisabled = Boolean(
      payload.fiscalNumber ||
      payloadCast.generatedAt ||
      payload.presettlementId ||
      payload.regions ||
      payload.status ||
      payload.documentationIncomplete ||
      payload.pickerId
    );
    yield put(preliquidationActions.setExportDisabled(!isExportDisabled));
  }
}

function* getDetailPreliquidations({
  payload,
}: PayloadAction<number>): Generator<
  | CallEffect<AxiosResponse<DetatilPreliquidationsApiResponse>>
  | PutEffect<{ type: string }>,
  void,
  DetatilPreliquidationsApiResponse
> {
  const response = yield call(
    preliquidationsMiddleware.getDetailPreliquidations,
    payload
  );
  if (response.status !== 200) {
    yield put(preliquidationActions.getDetailPreliquidationsError());
  } else {
    const { result } = response.data;
    yield put(preliquidationActions.getDetailPreliquidationsSuccess(result));
  }
}

function* getMorePreliquidations({
  payload,
}: PayloadAction<PreliquidationParamsMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<PreliquidationsApiResponse>>
  | PutEffect<{ type: string }>,
  void,
  PreliquidationsApiResponse
> {
  if (payload?.status === "") {
    delete payload["status"];
  }
  if (payload?.regions === "") {
    delete payload["regions"];
  }
  let payloadCast: PreliquidationCastParamsMiddlewareType =
    processDatePicker(payload);
  const response = yield call(
    preliquidationsMiddleware.getPreliquidations,
    payloadCast
  );
  if (response.status !== 200) {
    yield put(preliquidationActions.getMorePreliquidationsError());
  } else {
    const {
      result: { items },
      ...rest
    } = response.data;
    yield put(
      preliquidationActions.getMorePreliquidationsSuccess({ items, ...rest })
    );
  }
}

function* getInvoiceDetail({
  payload,
}: PayloadAction<string | undefined>): Generator<
  | PutEffect<{
      payload: DetailPreliquidationsContentResponseType;
      type: string;
    }>
  | PutEffect<{ payload: undefined; type: string }>
  | CallEffect<AxiosResponse<DetailPreliquidationsContentResponseType>>,
  void,
  DetailPreliquidationsApiResponseType
> {
  const response = yield call(
    preliquidationsMiddleware.getDetailInvoice,
    payload
  );
  if (response.status !== 200) {
    yield put(preliquidationActions.getInvoiceDetailError());
  } else {
    const { result } = response.data;
    yield put(
      preliquidationActions.getInvoiceDetailSuccess({
        ...result,
      })
    );
  }
}

function* putSaveDetailInvoice({
  payload,
}: PayloadAction<InvoiceFormType>): Generator<
  | PutEffect<{ payload: InvoiceFormType; type: string }>
  | PutEffect<{ type: string }>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | CallEffect<AxiosResponse<DetailPreliquidationsInvoiceApiResponseType>>,
  void,
  DetailPreliquidationsInvoiceApiResponseType
> {
  let result: DetailPreliquidationBodyParamsType = {
    emisionDate: payload.emisionDate || null,
    invoiceType: payload.invoiceType?.tag ? payload.invoiceType.tag : null,
    invoiceNumber: payload.invoiceNumber?.toUpperCase() || null,
    salePoint: payload.salePoint || null,
    caeNumber: payload.caeNumber || null,
  };
  result = process(result);

  const response = yield call(
    preliquidationsMiddleware.putSaveDetailInvoice,
    payload.presettlementId,
    result
  );
  if (response.status !== 200) {
    yield put(notificationActions.showNotification(connectionErrorAlert()));
    yield put(preliquidationActions.getInvoiceDetailSaveError());
  } else {
    const [, countryCode, , ,params] = location.pathname.split("/");
    const { preliquidations } = store.getState();
    const isBulkFlow = params === BULK_FLOW_ID;
    const { pendingApprovalPresettlements: ids, currentInvoice } =
      preliquidations;
    yield put(preliquidationActions.getInvoiceDetailSaveSuccess());
    if (!isBulkFlow || ids.length === 0 || currentInvoice === undefined) {
      yield put(replace(`/${countryCode}/presettlements`));
    } else if (currentInvoice < ids.length) {
      const id = ids[currentInvoice];
      yield put(preliquidationActions.setKeepDirty(false));
      yield put(preliquidationActions.getInvoiceDetailRequest(id.toString()));
    }
  }
}

function* goToNextInvoice(ids: number[], current: number) {
  const index = current + 1;
  const id = ids[index];
  yield put(preliquidationActions.setCurrentInvoice(index));
  yield put(preliquidationActions.getInvoiceDetailRequest(id.toString()));

  const title = document.getElementById("invoice-number");
  const rect = title?.getBoundingClientRect();
  if (rect) {
    const scroll = window.scrollY;
    window.scroll({
      top: scroll + rect.top - 100,
      left: 0,
      behavior: "smooth",
    });
  }
}

function* patchApproveDetailInvoice({
  payload,
}: PayloadAction<InvoiceFormType>): Generator<
  | PutEffect<{ payload: InvoiceFormType; type: string }>
  | PutEffect<{ type: string; payload: undefined }>
  | PutEffect<{ type: string }>
  | ReturnType<typeof goToNextInvoice>
  | CallEffect<AxiosResponse<DetailPreliquidationsInvoiceApiResponseType>>
  | AllEffect<
      CallEffect<
        AxiosResponse<DetailPreliquidationsInvoiceApiResponseType> | boolean
      >
    >,
  void,
  [DetailPreliquidationsInvoiceApiResponseType]
> {
  let result: DetailPreliquidationBodyParamsType = {
    emisionDate: payload.emisionDate || null,
    invoiceType: payload.invoiceType.tag,
    invoiceNumber: payload.invoiceNumber?.toUpperCase() || null,
    salePoint: payload.salePoint || null,
    caeNumber: payload.caeNumber || null,
  };
  const { presettlementId } = payload;
  result = process(result);

  const [response] = yield all([
    call(
      preliquidationsMiddleware.patchApproveDetailInvoice,
      presettlementId,
      result
    ),
    delay(FIRST_ANIMATION_TIME),
  ]);

  if (response.status !== 200) {
    yield put(preliquidationActions.getInvoiceDetailApproveError());
    yield delay(FIRST_ANIMATION_TIME + 1000);
    yield put(notificationActions.showNotification(connectionErrorAlert()));
    yield delay(200);
    yield put(
      preliquidationActions.setApprovingInvoiceState(LoadingButtonState.Idle)
    );
  } else {
    yield put(preliquidationActions.getInvoiceDetailApproveSuccess());
    yield delay(1.5 * FIRST_ANIMATION_TIME);
    const [, countryCode, , ,params] = location.pathname.split("/");
    const { preliquidations } = store.getState();
    const isBulkFlow = params === BULK_FLOW_ID;
    const { pendingApprovalPresettlements: invoiceIds, currentInvoice } =
      preliquidations;

    if (isBulkFlow && invoiceIds?.length > 0 && currentInvoice !== undefined) {
      if (currentInvoice >= invoiceIds.length - 1) {
        yield put(replace(`/${countryCode}/presettlements`));
      } else {
        yield put(
          preliquidationActions.setApprovingInvoiceState(
            LoadingButtonState.Idle
          )
        );
        yield goToNextInvoice(invoiceIds, currentInvoice);
      }
    } else {
      yield put(replace(`/${countryCode}/presettlements`));
    }
  }
}

function* putRejectDetailInvoice({
  payload,
}: PayloadAction<RejectInvoiceMiddlewareType>): Generator<
  | PutEffect<{ payload: InvoiceFormType; type: string }>
  | PutEffect<{ type: string }>
  | ReturnType<typeof goToNextInvoice>
  | CallEffect<AxiosResponse<DetailPreliquidationsInvoiceApiResponseType>>,
  void,
  DetailPreliquidationsInvoiceApiResponseType
> {
  const response = yield call(
    preliquidationsMiddleware.putRejectDetailInvoice,
    payload
  );

  if (response.status !== 200) {
    yield put(preliquidationActions.deleteInvoiceDetailError());
    if (response.data.statusCode === 30002) {
      yield put(
        preliquidationActions.toggleRejectInvoiceModalVisibility(false)
      );
      yield put(
        notificationActions.showNotification(
          connectionErrorAlert(payload.onErrorHandler)
        )
      );
    }
  } else {
    yield put(preliquidationActions.toggleRejectInvoiceModalVisibility(false));
    yield put(preliquidationActions.deleteInvoiceDetailSuccess());
    const [, countryCode, , ,params] = location.pathname.split("/");
    const { preliquidations } = store.getState();
    const isBulkFlow = params === BULK_FLOW_ID;
    const { pendingApprovalPresettlements: invoiceIds, currentInvoice } =
      preliquidations;

    if (isBulkFlow && invoiceIds?.length > 0 && currentInvoice !== undefined) {
      if (currentInvoice >= invoiceIds.length - 1) {
        yield put(replace(`/${countryCode}/presettlements`));
      } else {
        yield goToNextInvoice(invoiceIds, currentInvoice);
      }
    } else {
      yield put(replace(`/${countryCode}/presettlements`));
    }
  }
}

function* replaceInvoiceFile({
  payload,
}: PayloadAction<UploadInvoiceFileMiddlewareType>): Generator<
  | PutEffect<{ payload: string; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.replaceInvoiceFile,
    payload
  );
  if (response.status !== 200 && response.status !== 201) {
    yield put(preliquidationActions.replaceInvoiceFileError());
  } else {
    yield put(preliquidationActions.replaceInvoiceFileSuccess());
    yield put(preliquidationActions.setInvoiceFileStatus({ loading: false }));
    yield put(
      preliquidationActions.getInvoiceDetailRequest(payload.id.toString())
    );
  }
}

function* replaceInvoiceFileText({
  payload,
}: PayloadAction<UploadInvoiceFileMiddlewareType>): Generator<
  | PutEffect<{ payload: string; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.uploadInvoiceFileText,
    payload
  );
  if (!PRELIQUIDATION_STATUS_OK.includes(response.status)) {
    yield put(preliquidationActions.replaceInvoiceFileTextError());
    if (response.data.statusCode === 30009) {
      const description = resolveDescription(response.data.errors)
      yield put(
        notificationActions.showNotification({
          level: "error",
          title: t("invoice:modifyData"),
          body: t("invoice:failDataDescription", {
            description,
          }),
          onConfirmLabel: "invoice:manualUpload",
          onConfirm: () => {
            store.dispatch(
              preliquidationActions.replaceInvoiceFileFetch(payload)
            );
          },
        })
      );
    } else {
      yield put(
        notificationActions.showNotification({
          level: "error",
          title: t("invoice:invalidData"),
          body: t("invoice:manualUploadBody"),
          onConfirmLabel: "global:understood",
          onConfirm: () => {
            store.dispatch(
              preliquidationActions.replaceInvoiceFileFetch(payload)
            );
          },
        })
      );
    }
  } else {
    yield put(preliquidationActions.replaceInvoiceFileSuccess());
    yield put(preliquidationActions.setInvoiceFileStatus({ loading: false }));
    yield put(
      preliquidationActions.getInvoiceDetailRequest(payload.id.toString())
    );
  }
}

function* uploadInvoiceFile({
  payload,
}: PayloadAction<UploadInvoiceFileMiddlewareType>): Generator<
  | PutEffect<{ payload: string; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.uploadInvoiceFile,
    payload
  );
  if (response.status !== 200 && response.status !== 201) {
    yield put(preliquidationActions.uploadInvoiceFileError());
  } else {
    yield put(preliquidationActions.setInvoiceFileStatus({ loading: false }));
    yield put(preliquidationActions.uploadInvoiceFileSuccess());
    yield put(
      preliquidationActions.getInvoiceDetailRequest(payload.id.toString())
    );
  }
}

function* uploadInvoiceFileText({
  payload,
}: PayloadAction<UploadInvoiceFileMiddlewareType>): Generator<
  | PutEffect<{ payload: string; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.uploadInvoiceFileText,
    payload
  );
  if (!PRELIQUIDATION_STATUS_OK.includes(response.status)) {
    yield put(preliquidationActions.uploadInvoiceFileTextError());
    if (response.data.statusCode === 30009) {
      const description = resolveDescription(response.data.errors)
      yield put(
        notificationActions.showNotification({
          level: "error",
          title: t("invoice:modifyData"),
          body: t("invoice:failDataDescription", {
            description,
          }),
          onConfirmLabel: "invoice:manualUpload",
          onConfirm: () => {
            store.dispatch(
              preliquidationActions.uploadInvoiceFileFetch(payload)
            );
          },
        })
      );
    } else {
      yield put(
        notificationActions.showNotification({
          level: "error",
          title: t("invoice:invalidData"),
          body: t("invoice:manualUploadBody"),
          onConfirmLabel: "global:understood",
          onConfirm: () => {
            store.dispatch(
              preliquidationActions.uploadInvoiceFileFetch(payload)
            );
          },
        })
      );
    }
    // TODO HAcer las alertas
  } else {
    yield put(preliquidationActions.setInvoiceFileStatus({ loading: false }));
    yield put(preliquidationActions.uploadInvoiceFileSuccess());
    yield put(
      preliquidationActions.getInvoiceDetailRequest(payload.id.toString())
    );
  }
}

function* deleteInvoiceFile({
  payload,
}: PayloadAction<{ id: number }>): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: string | undefined; type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.deleteInvoiceFile,
    payload.id
  );
  if (response.status !== 204) {
    yield put(preliquidationActions.deleteInvoiceFileError());
  } else {
    yield put(preliquidationActions.deleteInvoiceFileSuccess());
    yield put(
      preliquidationActions.getInvoiceDetailRequest(payload.id.toString())
    );
  }
}

function* sendAccounting({
  payload,
}: PayloadAction<sendAccountinMiddlewareType>): Generator<
  | PutEffect<{ payload: sendAccountinMiddlewareType; type: string }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>,
  void,
  ApiResponse<void>
> {
  const response = yield call(
    preliquidationsMiddleware.sendAccounting,
    payload
  );
  if (response.status !== 204) {
    yield put(preliquidationActions.sendAccountingError());
  } else {
    yield put(preliquidationActions.sendAccountingSuccess());
    const state = store.getState();
    const { filters } = state.preliquidations;
    const { dispatch } = store;
    yield put(
      notificationActions.showNotification({
        level: "success",
        title: i18next.t("presettlements:successSendAccountingTitle"),
        body: i18next.t(
          `${
            payload.presettlements.length > 1
              ? "presettlements:successSendAccountingBody"
              : "presettlements:successSendAccountingBodySingular"
          }`
        ),
        onClose: () =>
          dispatch(
            preliquidationActions.getPreliquidationsRequest({
              ...filters,
              limit: 10,
              offset: 0,
            })
          ),
      })
    );
  }
}

function* getDetailInvoiceTypes(): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<DetailPreliquidationsInvoiceTypesApiResponseType>>,
  void,
  DetailPreliquidationsInvoiceTypesApiResponseType
> {
  const response = yield call(preliquidationsMiddleware.getDetailInvoiceTypes);
  if (response.status !== 200) {
    yield put(preliquidationActions.getInvoiceDetailTypesError());
  } else {
    const { result } = response.data;
    yield put(preliquidationActions.getInvoiceDetailTypesSuccess(result));
  }
}

function* getRejectedReason(): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<RejectInvoiceReasonType[]>>>
  | PutEffect<{ payload: RejectInvoiceReasonType[]; type: string }>
  | PutEffect<{ payload: undefined; type: string }>,
  void,
  AxiosResponse<ApiResponseResult<RejectInvoiceReasonType[]>>
> {
  const response = yield call(preliquidationsMiddleware.getRejectedReason);
  if (response.status !== 200) {
    yield put(preliquidationActions.getRejectedReasonError());
  } else {
    const { result } = response.data;
    yield put(preliquidationActions.getRejectedReasonSuccess(result));
  }
}

function* adjustAmount({
  payload,
}: PayloadAction<AdjustAmountMiddlewareType>): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<void>>>
  | AllEffect<CallEffect<AxiosResponse<ApiResponse<void>> | boolean>>,
  void,
  [ApiResponse<AdjustmenResponseType>]
> {
  const [response] = yield all([
    call(preliquidationsMiddleware.preliquidationAdjustment, payload),
    delay(FIRST_ANIMATION_TIME),
  ]);

  if (response.status !== 200) {
    yield put(preliquidationActions.adjustAmountError());
    if (response.data.statusCode === 30020) {
      yield delay(2500);
      yield put(preliquidationActions.toggleModalVisibility(false));
      yield put(
        notificationActions.showNotification({
          id: "notification-concurrency-error",
          level: "error",
          title: i18next.t("presettlements:concurrencyTitle"),
          body: i18next.t("presettlements:concurrencyMessage"),
          onClose: () => payload.callback(payload.id),
        })
      );
    }
  } else {
    yield put(preliquidationActions.adjustAmountSuccess());
    yield put(preliquidationActions.toggleModalVisibility(false));
    payload.callback(payload.id);
  }
}

function* getPendingApprovalPresettlements(): Generator<
  | CallEffect<AxiosResponse<PreliquidationsApiResponse>>
  | PutEffect<{ type: string }>,
  void,
  PreliquidationsApiResponse
> {
  const response = yield call(preliquidationsMiddleware.getPreliquidations, {
    limit: 200,
    offset: 0,
    status: "in_approvement",
  });
  if (response.status !== 200) {
    yield put(preliquidationActions.getPendingApprovalPresettlementsError());
  } else {
    const ids = response.data?.result?.items?.map((p) => p.id);
    if (ids?.length === 0) {
      yield put(
        notificationActions.showNotification({
          id: "notification-quantity-error",
          level: "error",
          title: i18next.t("presettlements:noInvoicesTitle"),
          body: i18next.t("presettlements:noInvoicesMessage"),
        })
      );
      yield put(
        preliquidationActions.getPendingApprovalPresettlementsSuccess([])
      );
    } else {
      const [, countryCode] = location.pathname.split("/");
      yield put(
        preliquidationActions.getPendingApprovalPresettlementsSuccess(ids || [])
      );
      yield put(replace(`/${countryCode}/presettlements/detail/approvement?tab=invoice`));
    }
  }
}

function resolveDescription(errors: string[] | undefined): string | undefined {
  return errors
    ?.map((error) => {
      const errorSplit = error.split("-");
      const errorKey = errorSplit[0].trim();
      const errorMessage = errorSplit[1] !== undefined ? errorSplit[1] : "";

      return !errorMessage
        ? `<b>${i18next.t(`invoice:${errorKey}`)}</b>`
        : i18next.t(`invoice:${errorKey}`, {
          errorValue: errorMessage,
        });

    })
    .join("\n");
}
